import React, { ReactNode, useEffect, useMemo, useRef } from 'react';
import {
  createMonitoringReportContext,
  MonitoringReportProvider,
  type MonitoringReportContextType,
  type MonitoringReportAPIAdapter,
} from '../../shared';
import { buildMagicActionItemsHtml, getNextActionItemOrder } from '../sections/action-items.util';
import { ReportPhotoDTO } from '../../shared/photos/types';
import { MagicReportState } from '../magic-report.type';
import { getFileUploadUrl } from '../../../project/project.api';
import {
  exportMagicReportPdf,
  getPhotos as apiGetPhotos,
  getSignatureAsBase64,
  getImageAsBase64,
  uploadPhoto as apiUploadPhoto,
  batchUpdatePhotos as apiBatchUpdatePhotos,
  deletePhoto as apiDeletePhoto,
  updateEditedPhoto as apiUpdateEditedPhoto,
  updateMagicReport,
  updateMagicReportSections,
} from '../magic-report.api';
import { ReportExportUtil, type ExportReportData, type ExportSummary } from '../../shared/utils/export.util';
import { toApiDrawScopedSections } from '../sections/draw-scoped-sections.util';

const MagicReportContext = createMonitoringReportContext<ReportPhotoDTO, { magicReportId?: string }>();

export interface MagicReportContextType extends Omit<
  MonitoringReportContextType<ReportPhotoDTO>,
  'reportState' | 'setReportState' | 'initialState'
> {
  reportState: MagicReportState;
  setReportState: React.Dispatch<React.SetStateAction<MagicReportState>>;
  initialState: MagicReportState;
  registerSectionSync: (sectionId: string, syncFn: () => Promise<void>) => void;
  unregisterSectionSync: (sectionId: string) => void;
  runAllSectionSyncs: () => Promise<void>;
  registerSectionFlush: (sectionId: string, flushFn: () => void) => void;
  unregisterSectionFlush: (sectionId: string) => void;
  flushAllPendingUpdates: () => void;
  claimActionItemOrder: () => number;
  resetActionItemOrderCounter: () => void;
  claimOneTimeSeed: (key: string) => boolean;
  registerSectionOrderSync: (sectionId: string, syncFn: (updatedSections: any) => void) => void;
  unregisterSectionOrderSync: (sectionId: string) => void;
  syncAllSectionOrders: (updatedSections: any) => void;
  lastSavedAt: Date | null;
  autoSaveEnabled: boolean;
  setAutoSaveEnabled: (enabled: boolean) => void;
  currentStep: number;
  setCurrentStep: (step: number) => void;
}

export const useMagicReport = (): MagicReportContextType => {
  const context = React.useContext(MagicReportContext);
  if (!context) throw new Error('useMagicReport must be used within a MagicReportProvider');
  return context as unknown as MagicReportContextType;
};

interface MagicReportProviderProps {
  children: ReactNode;
  initialData: MagicReportState;
  projectId: string;
  magicReportId: string;
}

const createMagicReportAPI = (): MonitoringReportAPIAdapter<ReportPhotoDTO> => ({
  getPhotos: async (reportId: string) => {
    const photos = await apiGetPhotos(reportId);
    return photos.map(photo => ({ ...photo, reportId: photo.reportId || reportId }));
  },
  getPhotoUrl: (blobName: string) => getFileUploadUrl(blobName),
  updateReport: async (reportId: string, data: any) => {
    // Persist core report data (summary, invoices, site conditions, etc.) if provided
    if (
      data.summary !== undefined ||
      data.previousFundingsNote !== undefined ||
      data.coverPage !== undefined ||
      data.siteConditions !== undefined ||
      data.invoiceData !== undefined ||
      data.highlightedLineItemIds !== undefined
    ) {
      await updateMagicReport(reportId, {
        name: data.name,
        summary: data.summary,
        previousFundingsNote: data.previousFundingsNote,
        coverPage: data.coverPage,
        siteConditions: data.siteConditions,
        invoiceData: data.invoiceData,
        highlightedLineItemIds: data.highlightedLineItemIds,
        // Note: invoices are handled separately by the backend
      });
    }

    // Persist dynamic sections if provided (independent save)
    if (data.sections) {
      try {
        // Convert MagicReportSectionsDTO to UpdateMagicReportSectionsDTO
        // Note: Section IDs in state use kebab-case (e.g., 'application-comments'),
        // but DTO keys use camelCase (e.g., 'applicationComments')
        // Draw-scoped sections are stored per draw under `<key>__<drawId>` and must be sent back
        // as the COMPLETE array — the endpoint replaces these fields wholesale, so omitting a
        // draw would delete its comments. `drawOrder` keeps the stored order stable.
        const drawOrder = (data.draws || []).map((draw: any) => draw.drawId);
        const sectionsUpdate = {
          previousFundings: data.sections['previous-fundings'] || data.sections.previousFundings,
          applicationComments: toApiDrawScopedSections(data.sections, 'application-comments', drawOrder),
          reductionComments: toApiDrawScopedSections(data.sections, 'reduction-comments', drawOrder),
          schedule: data.sections.schedule,
          notaryAndSignatures: data.sections.notaryAndSignatures,
          planCostReviewIssues: data.sections.planCostReviewIssues,
          materialTestingReports: data.sections.materialTestingReports,
          constructionMeetingMinutes: data.sections.constructionMeetingMinutes,
          foundationSurvey: data.sections.foundationSurvey,
          progressDescription: data.sections.progressDescription,
          projectDescription: data.sections.projectDescription,
          minorAdjustments: data.sections.minorAdjustments,
          docsReceived: data.sections.docsReceived,
          retainage: data.sections.retainage,
          storedMaterials: data.sections.storedMaterials,
          reallocations: data.sections.reallocations,
          changeOrders: data.sections.changeOrders,
          potentialChangeOrders: data.sections.potentialChangeOrders,
          lienWaivers: data.sections.lienWaivers,
          dynamicSections: data.sections.dynamicSections,
          scheduleGraphRows: data.sections.scheduleGraph,
          scheduleGraphAxis: data.sections.scheduleGraphAxis,
        };

        await updateMagicReportSections(reportId, sectionsUpdate);
      } catch (err) {
        console.error('Failed to update magic report sections', err);
        throw err; // Re-throw to show error to user
      }
    }
  },
  buildReportHtml: async (state: any) => {
    const exporter = new ReportExportUtil(
      apiGetPhotos as any,
      getFileUploadUrl as any,
      getSignatureAsBase64 as any,
      async (photos: any[]) => {
        const { processPhotosForExport } = await import('../../shared/photos/utils');
        return processPhotosForExport(photos, getFileUploadUrl as any);
      },
    );

    const reportData: ExportReportData = {
      projectSummary: state.projectSummary,
      draws: state.draws,
      hiddenCoLienDrawIds: state.hiddenCoLienDrawIds,
      recapApplications: state.recapApplications,
      // Primary-draw mirrors, for the printed blocks that are not yet per-draw.
      drawSummary: state.drawSummary,
      budgetSummary: state.budgetSummary,
      author: state.author,
      lineItems: state.lineItems,
      reportId: state.reportId,
      coverPagePhotoIds: state.coverPagePhotoIds || [],
      sections: state.sections,
      coverPage: state.coverPage,
      siteConditions: state.siteConditions,
      previousFundingsNote: (state as any).previousFundingsComment,
    } as any;

    const summary: ExportSummary = {
      coverLetter: state.summary?.coverLetter,
      executiveSummary: state.summary?.executiveSummary,
      actionItems: state.summary?.actionItems,
    };

    return exporter.buildFullReportHtml(reportData, summary, 'magic');
  },
  buildPhotosOnlyHtml: async (reportId: string, options?: { projectName?: string; date?: Date }) => {
    const exporter = new ReportExportUtil(
      apiGetPhotos as any,
      getFileUploadUrl as any,
      getSignatureAsBase64 as any,
      async (photos: any[]) => {
        const { processPhotosForExport } = await import('../../shared/photos/utils');
        return processPhotosForExport(photos, getFileUploadUrl as any);
      },
    );
    return exporter.buildPhotosOnlyHtml(reportId, options);
  },
  exportPdf: exportMagicReportPdf,
  uploadPhoto: async (reportId: string, file: File, onProgress?: (progress: number) => void, sortOrder?: number) => {
    const resp = await apiUploadPhoto(reportId, file, onProgress, sortOrder);
    // Adapt to generic PhotoUploadResponse shape (match limited report mapping)
    return {
      id: (resp as any).id ?? (resp as any).photoId,
      blobName: (resp as any).pdfBlobName ?? (resp as any).blobName ?? (resp as any).originalBlobName,
      originalBlobName: (resp as any).originalBlobName ?? (resp as any).blobName,
      pdfBlobName: (resp as any).pdfBlobName,
      thumbnailBlobName: (resp as any).thumbnailBlobName,
      fileName: file.name,
      fileSize: file.size,
      width: (resp as any).width ?? 0,
      height: (resp as any).height ?? 0,
      sortOrder: (resp as any).sortOrder ?? 0,
      reportId,
      caption: (resp as any).caption ?? '',
      hasCaption: (resp as any).hasCaption ?? false,
      createdOn: (resp as any).createdOn,
      updatedOn: (resp as any).updatedOn,
    } as any;
  },
  batchUpdatePhotos: apiBatchUpdatePhotos as any,
  deletePhoto: apiDeletePhoto as any,
  updateEditedPhoto: apiUpdateEditedPhoto as any,
  getImageAsBase64: getImageAsBase64 as any,
});

const MagicReportSyncManager: React.FC<{ children: ReactNode }> = ({ children }) => {
  const sectionSyncsRef = useRef<Map<string, () => Promise<void>>>(new Map());
  const sectionFlushFunctionsRef = useRef<Map<string, () => void>>(new Map());
  const sectionOrderSyncsRef = useRef<Map<string, (updatedSections: any) => void>>(new Map());
  // Shared monotonic counter so every section claims a unique slot in booking order.
  // Seeded from committed state the first time any section calls claimActionItemOrder.
  const sharedOrderCounterRef = useRef<number | null>(null);

  // One-shot guards for section defaults that must be applied once per report rather than once per
  // mount. A ref inside a section component is scoped to the mount, so a section that seeds a
  // default there re-seeds it every time the user leaves and returns to its wizard step, undoing
  // whatever the user changed in between. This provider lives above the steps and remounts only
  // when a different report is opened, which is exactly the scope those defaults need.
  const seededOnceRef = useRef<Set<string>>(new Set());

  const registerSectionSync = React.useCallback((sectionId: string, syncFn: () => Promise<void>) => {
    sectionSyncsRef.current.set(sectionId, syncFn);
  }, []);

  const unregisterSectionSync = React.useCallback((sectionId: string) => {
    sectionSyncsRef.current.delete(sectionId);
  }, []);

  const runAllSectionSyncs = React.useCallback(async () => {
    const syncPromises = Array.from(sectionSyncsRef.current.values()).map(syncFn => syncFn());
    await Promise.all(syncPromises);
  }, []);

  const registerSectionFlush = React.useCallback((sectionId: string, flushFn: () => void) => {
    sectionFlushFunctionsRef.current.set(sectionId, flushFn);
  }, []);

  const unregisterSectionFlush = React.useCallback((sectionId: string) => {
    sectionFlushFunctionsRef.current.delete(sectionId);
  }, []);

  const flushAllPendingUpdates = React.useCallback(() => {
    sectionFlushFunctionsRef.current.forEach(flushFn => flushFn());
  }, []);

  // Inject these functions into the context
  const context = React.useContext(MagicReportContext);

  // Reset the counter when the active report changes so the next claimActionItemOrder
  // reseeds from the new report's committed state instead of carrying over stale values.
  useEffect(() => {
    const reportId = (context as any)?.reportId;
    if (!reportId) return;
    sharedOrderCounterRef.current = null;
  }, [(context as any)?.reportId]);

  const claimActionItemOrder = React.useCallback((): number => {
    if (sharedOrderCounterRef.current === null) {
      // Seed once from committed reportState so we start above any persisted orders
      sharedOrderCounterRef.current = getNextActionItemOrder((context as any)?.reportState?.sections);
    }
    return sharedOrderCounterRef.current++;
  }, [context]);

  const resetActionItemOrderCounter = React.useCallback(() => {
    sharedOrderCounterRef.current = null;
  }, []);

  /** Returns true for the first call with a given key on this report, false for every later one. */
  const claimOneTimeSeed = React.useCallback((key: string): boolean => {
    if (seededOnceRef.current.has(key)) return false;
    seededOnceRef.current.add(key);
    return true;
  }, []);

  const registerSectionOrderSync = React.useCallback((sectionId: string, syncFn: (updatedSections: any) => void) => {
    sectionOrderSyncsRef.current.set(sectionId, syncFn);
  }, []);

  const unregisterSectionOrderSync = React.useCallback((sectionId: string) => {
    sectionOrderSyncsRef.current.delete(sectionId);
  }, []);

  const syncAllSectionOrders = React.useCallback((updatedSections: any) => {
    sectionOrderSyncsRef.current.forEach(syncFn => syncFn(updatedSections));
  }, []);

  // Set the flush function ref so saveReport can call it
  useEffect(() => {
    if (context && (context as any).flushPendingUpdatesRef) {
      (context as any).flushPendingUpdatesRef.current = flushAllPendingUpdates;
    }
    return () => {
      if (context && (context as any).flushPendingUpdatesRef) {
        (context as any).flushPendingUpdatesRef.current = null;
      }
    };
  }, [context, flushAllPendingUpdates]);

  if (context) {
    (context as any).registerSectionSync = registerSectionSync;
    (context as any).unregisterSectionSync = unregisterSectionSync;
    (context as any).runAllSectionSyncs = runAllSectionSyncs;
    (context as any).registerSectionFlush = registerSectionFlush;
    (context as any).unregisterSectionFlush = unregisterSectionFlush;
    (context as any).flushAllPendingUpdates = flushAllPendingUpdates; // Keep for backward compatibility
    (context as any).claimActionItemOrder = claimActionItemOrder;
    (context as any).resetActionItemOrderCounter = resetActionItemOrderCounter;
    (context as any).claimOneTimeSeed = claimOneTimeSeed;
    (context as any).registerSectionOrderSync = registerSectionOrderSync;
    (context as any).unregisterSectionOrderSync = unregisterSectionOrderSync;
    (context as any).syncAllSectionOrders = syncAllSectionOrders;
  }

  return <>{children}</>;
};

const MagicReportSummarySync: React.FC<{ children: ReactNode; summary?: MagicReportState['summary'] }> = ({ children, summary }) => {
  const { reportState, setReportState } = useMagicReport();
  const lastSyncedSummaryRef = useRef<string | null>(null);

  useEffect(() => {
    const normalizeSummary = (value?: MagicReportState['summary']) => {
      if (!value) return null;
      return {
        coverLetter: (value.coverLetter || '').trim(),
        executiveSummary: (value.executiveSummary || '').trim(),
        actionItems: (value.actionItems || '').trim(),
      };
    };

    const normalizedInitial = normalizeSummary(summary);
    const serializedInitial = JSON.stringify(normalizedInitial);

    if (serializedInitial === lastSyncedSummaryRef.current) {
      return;
    }

    lastSyncedSummaryRef.current = serializedInitial;

    if (!summary) return;

    const normalizedCurrent = normalizeSummary(reportState.summary);
    const serializedCurrent = JSON.stringify(normalizedCurrent);

    if (serializedInitial !== serializedCurrent) {
      setReportState((prev: MagicReportState) => ({ ...prev, summary }));
    }
  }, [summary, reportState.summary, setReportState]);

  return <>{children}</>;
};

const MagicReportActionItemsSync: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { reportState, setReportState } = useMagicReport();

  const derivedActionItems = useMemo(
    () =>
      buildMagicActionItemsHtml(
        reportState.sections,
        reportState.draws,
        (reportState as any).previousFundingsComment,
        reportState.hiddenCoLienDrawIds,
      ),
    [reportState.sections, reportState.draws, (reportState as any).previousFundingsComment, reportState.hiddenCoLienDrawIds],
  );

  useEffect(() => {
    const current = (reportState.summary?.actionItems || '').trim();
    const next = (derivedActionItems || '').trim();
    if (current === next) return;
    setReportState((prev: MagicReportState) => ({ ...prev, summary: { ...(prev.summary || {}), actionItems: derivedActionItems } }));
  }, [derivedActionItems, reportState.summary?.actionItems, setReportState]);

  return <>{children}</>;
};

const MagicReportAutoSave: React.FC<{ children: ReactNode }> = ({ children }) => {
  const context = React.useContext(MagicReportContext);
  const [autoSaveEnabled, setAutoSaveEnabled] = React.useState(true);
  const [lastSavedAt, setLastSavedAt] = React.useState<Date | null>(null);
  const [currentStep, setCurrentStep] = React.useState(0);
  const lastSaveRef = useRef<Date | null>(null);

  // Inject auto-save state into context
  if (context) {
    (context as any).autoSaveEnabled = autoSaveEnabled;
    (context as any).setAutoSaveEnabled = setAutoSaveEnabled;
    (context as any).lastSavedAt = lastSavedAt;
    (context as any).currentStep = currentStep;
    (context as any).setCurrentStep = setCurrentStep;
  }

  useEffect(() => {
    if (!context || !autoSaveEnabled) return;

    // Auto-save only on Payapp Review and Document Review steps (indices follow MagicReportWizard STEPS)
    const PAYAPP_REVIEW_STEP_INDEX = 2;
    const DOCUMENT_REVIEW_STEP_INDEX = 3;
    if (currentStep !== PAYAPP_REVIEW_STEP_INDEX && currentStep !== DOCUMENT_REVIEW_STEP_INDEX) {
      return;
    }

    const { isDirty, hasUnsavedPhotoChanges, isSaving, saveReport, setReportState } = context as any;

    // Auto-save interval: every 30 seconds (only on Payapp / Document review steps)
    const interval = setInterval(async () => {
      const hasChanges = isDirty || hasUnsavedPhotoChanges;
      if (hasChanges && !isSaving) {
        try {
          await saveReport();
          const now = new Date();
          setLastSavedAt(now);
          lastSaveRef.current = now;
          // Update reportState with lastSavedAt
          setReportState((prev: any) => ({ ...prev, lastSavedAt: now }));
        } catch (error) {
          console.error('Auto-save failed:', error);
        }
      }
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [context, autoSaveEnabled, currentStep]);

  // Listen to manual saves (by monitoring isSaving changes)
  useEffect(() => {
    if (!context) return;
    const { isSaving, setReportState } = context as any;

    // When isSaving changes from true to false, it means a save just completed
    if (!isSaving && lastSaveRef.current) {
      const timeSinceLastUpdate = Date.now() - (lastSaveRef.current?.getTime() || 0);
      // If it's been more than 1 second since our last update, this is likely a manual save
      if (timeSinceLastUpdate > 1000) {
        const now = new Date();
        setLastSavedAt(now);
        lastSaveRef.current = now;
        setReportState((prev: any) => ({ ...prev, lastSavedAt: now }));
      }
    }
  }, [context, (context as any)?.isSaving]);

  return <>{children}</>;
};

export const MagicReportProvider: React.FC<MagicReportProviderProps> = ({ children, initialData, projectId, magicReportId }) => {
  const api = React.useMemo(() => createMagicReportAPI(), []);
  const enrichedInitialData: MagicReportState = { ...initialData, reportId: magicReportId };

  return (
    <MonitoringReportProvider
      context={MagicReportContext}
      initialData={enrichedInitialData}
      projectId={projectId}
      reportId={magicReportId}
      reportFieldName='magicReportId'
      reportType='magic'
      api={api}
    >
      <MagicReportSyncManager>
        <MagicReportAutoSave>
          <MagicReportSummarySync summary={enrichedInitialData.summary}>
            <MagicReportActionItemsSync>{children}</MagicReportActionItemsSync>
          </MagicReportSummarySync>
        </MagicReportAutoSave>
      </MagicReportSyncManager>
    </MonitoringReportProvider>
  );
};
